import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { getServiceConfiguration } from '@tmap-web-lib/remote-api-client/frontman'

const NEXT_PUBLIC_FRONTMAN_URL = process.env.NEXT_PUBLIC_FRONTMAN_URL as string
const NEXT_PUBLIC_BUILD_VERSION = process.env.NEXT_PUBLIC_BUILD_VERSION as string
const NEXT_PUBLIC_BUILD_ENV = process.env.NEXT_PUBLIC_BUILD_ENV as string
const TARGET_BUILD_ENV = process.env.NODE_ENV === 'production' ? NEXT_PUBLIC_BUILD_ENV : 'local'

type DatadogRum = Omit<typeof datadogRum, 'version'>
type PickedRumMethod = keyof Pick<
  DatadogRum,
  'setUser'
  | 'setGlobalContextProperty'
  | 'addAction'
>
type InferMethodArgs<Method extends keyof DatadogRum> = Parameters<DatadogRum[Method]>

export const rum: {
  init: (userId: string) => void
  addError: (error: unknown, context?: object) => void
} & { [Method in PickedRumMethod]: DatadogRum[Method] } = {
  init(userId: string) {
    getServiceConfiguration({ key: 'MY_DATADOG_CONFIG' })
      .then(({ data }) => {
        const configRaw = data?.data?.contents[0]?.data

        const parsedConfig: { sampleRate?: number; sessionReplayRate?: number; targetList?: string[] } =
          (configRaw as { sampleRate?: number; sessionReplayRate?: number; targetList?: string[] } | undefined) ?? {}

        const sampleRateValueConfig = parsedConfig.sampleRate ?? NaN
        const sessionReplayRateConfig = parsedConfig.sessionReplayRate ?? NaN
        const targetListConfig = parsedConfig.targetList
        let sessionSampleRate = TARGET_BUILD_ENV !== 'PRD'
          ? 100
          : (isNaN(sampleRateValueConfig) ? 1 : sampleRateValueConfig)
        let sessionReplaySampleRate = TARGET_BUILD_ENV !== 'PRD'
          ? 100
          : (isNaN(sessionReplayRateConfig) ? 0 : sessionReplayRateConfig)
        if (new Set(targetListConfig ?? []).has(String(userId))) {
          sessionSampleRate = 100
          sessionReplaySampleRate = 100
        }
        callRumMethod('init', [
          {
            applicationId: '14b33cf8-39d6-4c56-a39d-b8cf753f75cc',
            clientToken: 'puba0f027eafb76d52939548d313d09d0d6',
            site: 'datadoghq.com',
            service: 'tmap-my-fe',
            env: TARGET_BUILD_ENV.toLowerCase(),
            version: NEXT_PUBLIC_BUILD_VERSION,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            allowedTracingUrls: [NEXT_PUBLIC_FRONTMAN_URL],
            sessionSampleRate,
            sessionReplaySampleRate,
            beforeSend: (event) => {
              if (event.type === 'error') {
                switch (event.error.type) {
                case 'CanceledError':
                case 'IgnoredEventCancel':
                  return false
                }
              }
              return true
            },
          }
        ])
        this.setGlobalContextProperty('userAgent', window.navigator.userAgent)
        this.setUser({ id: userId })

        datadogLogs.init({
          clientToken: 'puba0f027eafb76d52939548d313d09d0d6',
          site: 'datadoghq.com',
          service: 'tmap-my-fe',
          env: TARGET_BUILD_ENV.toLowerCase(),
          version: NEXT_PUBLIC_BUILD_VERSION,
          forwardErrorsToLogs: false,
          sessionSampleRate: 100,
        })
        datadogLogs.setUser({ id: userId })
      })
  },
  setGlobalContextProperty(...args: InferMethodArgs<'setGlobalContextProperty'>) {
    callRumMethod('setGlobalContextProperty', args)
  },
  setUser(...args: InferMethodArgs<'setUser'>) {
    callRumMethod('setUser', args)
  },
  addAction(...args: InferMethodArgs<'addAction'>) {
    callRumMethod('addAction', args)
  },
  addError(error: unknown, context?: object) {
    const normalized = error instanceof Error ? error : String(error)
    logError(normalized, context)
  },
}

function callRumMethod<Method extends keyof DatadogRum>(method: Method, args?: InferMethodArgs<Method>) {
  import('@datadog/browser-rum').then(({ datadogRum }) => {
    (datadogRum[method] as any).apply(datadogRum, args)
  })
}

export function logError(error: Error | string, context?: object) {
  const message = error instanceof Error ? error.message : error
  const ctx = error instanceof Error ? { ...context, stack: error.stack } : context
  datadogLogs.logger.error(message, ctx)
}
